import { useState } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
  Link,
} from '@tanstack/react-router';
import {
  useListAgentSkills,
  useUpdateAgentSkill,
  useDeleteAgentSkill,
  useExportSkill,
  useForkTemplate,
  useCreateAgentSkill,
  useDisabledSkills,
  useToggleSkillDisabled,
} from '../../api/agent-skills';
import type { CreateAgentSkillInput } from '../../api/agent-skills';
import { useUserProfile } from '../../api/profile';
import { useIsAdmin } from '../../components/onboarding-new/api';
import { useChatStore } from '../../components/Pleri/store/chatStore';
import { SkillDetail } from '../settings/skills/components/SkillDetail';
import { SkillForm } from '../settings/skills/components/SkillForm';
import { ConfirmDialog } from '../settings/skills/components/ConfirmDialog';
import {
  decodeSkillRef,
  encodeSkillRef,
} from '../settings/skills/utils/skillRef';
import { isSkillReadOnly } from '../settings/skills/utils/skillAccess';
import { getApiErrorMessage } from '../../lib/utils';

import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/skills/$skillRef')({
  component: SkillDetailPage,
});

function SkillDetailPage() {
  const { t } = useTranslation('pleri/skills');
  const { skillRef } = useParams({ from: '/skills/$skillRef' });
  const navigate = useNavigate();
  const { scope, name } = decodeSkillRef(skillRef);

  const { data: skills, isLoading } = useListAgentSkills();
  const { data: userProfile } = useUserProfile();
  const isAdmin = useIsAdmin();
  const { data: disabledSkillIds = [] } = useDisabledSkills();

  const updateSkill = useUpdateAgentSkill();
  const deleteSkill = useDeleteAgentSkill();
  const exportSkill = useExportSkill();
  const forkTemplate = useForkTemplate();
  const createSkill = useCreateAgentSkill();
  const toggleDisabled = useToggleSkillDisabled();

  const [isEditing, setIsEditing] = useState(false);
  const [isCopyingToTenant, setIsCopyingToTenant] = useState(false);
  const [isCopyingToMySkills, setIsCopyingToMySkills] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [copyToTenantError, setCopyToTenantError] = useState<string | null>(null);
  const [copyToMySkillsError, setCopyToMySkillsError] = useState<string | null>(null);

  // Guard: valid skillRefs always encode as "scope--name". If there's no
  // separator the router matched this dynamic segment instead of a static
  // route (e.g. /skills/library). Redirect to the correct path. It sits
  // below every hook so the hook order never changes between renders.
  if (!scope) {
    if (skillRef === 'library') {
      navigate({ to: '/skills/library' });
    } else {
      navigate({ to: '/skills' });
    }
    return null;
  }

  const currentUserId = userProfile?.id ?? null;

  const skill = (skills ?? []).find(
    (s) => s.scope === scope && s.name === name,
  );

  const tagNames = Array.from(
    new Set((skills ?? []).flatMap((s) => s.tags ?? [])),
  ).sort();

  const isReadOnly = !skill || isSkillReadOnly(skill, { currentUserId, isAdmin });

  const getDisableId = () =>
    skill?.isBuiltIn ? `builtin-${skill.name}` : (skill?.name ?? '');

  const isDisabledByUser = disabledSkillIds.includes(getDisableId());

  const handleToggleEnabled = async () => {
    if (!skill || skill.isBuiltIn) {
      return;
    }
    try {
      await updateSkill.mutateAsync({
        scope: skill.scope,
        name: skill.name,
        enabled: !skill.enabled,
      });
    } catch (err) {
      setActionError(
        getApiErrorMessage(
          err,
          `Failed to ${skill.enabled ? 'disable' : 'enable'} skill`,
        ),
      );
    }
  };

  const handleToggleUserDisabled = () => {
    if (!skill) {
      return;
    }
    const disableId = getDisableId();
    const updated = isDisabledByUser
      ? disabledSkillIds.filter((id) => id !== disableId)
      : [...disabledSkillIds, disableId];
    toggleDisabled.mutate(updated, {
      onError: (err) =>
        setActionError(
          getApiErrorMessage(err, 'Failed to update skill visibility'),
        ),
    });
  };

  const handleUpdate = async (form: CreateAgentSkillInput) => {
    if (!skill) {
      return;
    }
    setUpdateError(null);
    try {
      const { scope: _s, name: _n, ...rest } = form;
      await updateSkill.mutateAsync({
        scope: skill.scope,
        name: skill.name,
        ...rest,
      });
      setIsEditing(false);
    } catch (err) {
      setUpdateError(getApiErrorMessage(err, 'Failed to update skill'));
    }
  };

  const handleDelete = async () => {
    if (!skill) {
      return;
    }
    setDeleteError(null);
    try {
      await deleteSkill.mutateAsync({ scope: skill.scope, name: skill.name });
      setConfirmDelete(false);
      navigate({ to: '/skills' });
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, 'Failed to delete skill'));
    }
  };

  const handleExport = async () => {
    if (!skill) {
      return;
    }
    try {
      const result = await exportSkill.mutateAsync({
        skill: { scope: skill.scope, name: skill.name },
      });
      if (result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Failed to export skill'));
    }
  };

  const handleFork = async () => {
    if (!skill) {
      return;
    }
    try {
      const result = await forkTemplate.mutateAsync({
        skill: { scope: skill.scope, name: skill.name },
      });
      navigate({
        to: '/skills/$skillRef',
        params: { skillRef: encodeSkillRef(result.scope, result.name) },
      });
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Failed to fork template'));
    }
  };

  const handleCopyToTenant = async (form: CreateAgentSkillInput) => {
    if (!skill) {
      return;
    }
    setCopyToTenantError(null);
    try {
      const result = await createSkill.mutateAsync(form);
      setIsCopyingToTenant(false);
      navigate({
        to: '/skills/$skillRef',
        params: { skillRef: encodeSkillRef(result.scope, result.name) },
      });
    } catch (err) {
      setCopyToTenantError(
        getApiErrorMessage(err, 'Failed to copy skill to tenant'),
      );
    }
  };

  const handleCopyToMySkills = async (form: CreateAgentSkillInput) => {
    if (!skill) {
      return;
    }
    setCopyToMySkillsError(null);
    try {
      const result = await createSkill.mutateAsync(form);
      setIsCopyingToMySkills(false);
      navigate({
        to: '/skills/$skillRef',
        params: { skillRef: encodeSkillRef(result.scope, result.name) },
      });
    } catch (err) {
      setCopyToMySkillsError(
        getApiErrorMessage(err, 'Failed to copy skill to My Skills'),
      );
    }
  };

  const handleTryInChat = () => {
    if (!skill) {
      return;
    }
    const store = useChatStore.getState();
    const conversationId = store.createConversation();
    store.setInputPrompt(`Execute the "${skill.displayName}" skill.`);
    store.submitInputPrompt();
    navigate({ to: '/c/$conversationId', params: { conversationId } });
  };

  const handleEditWithChat = () => {
    if (!skill) {
      return;
    }
    const store = useChatStore.getState();
    const conversationId = store.createConversation();
    store.setInputPrompt(`Pleri help me update this skill "${skill.displayName}". Ask me questions about what needs to be improved.`);
    store.submitInputPrompt();
    navigate({ to: '/c/$conversationId', params: { conversationId } });
  };

  if (isLoading && !skills) {
    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-gray-900">
        <div className="flex-1 p-6">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                style={{ width: `${80 - i * 8}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-white dark:bg-gray-900">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('skillNotFound')}
        </p>
        <Link
          to="/skills"
          className="mt-3 text-sm text-orange-600 hover:text-orange-700"
        >
          {t('backToSkills')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-gray-900">
      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('deleteSkill')}
        message={t('areYouSureYouWantToDeleteSkill', {
          displayName: skill.displayName,
        })}
        confirmLabel={t('deleteSkill')}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteError(null);
        }}
      />
      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-4xl">
          {isEditing ? (
            <div className="p-6">
              <SkillForm
                initial={{
                  name: skill.name,
                  displayName: skill.displayName,
                  description: skill.description,
                  workspace: skill.workspace ?? [],
                  instructions: skill.instructions,
                  scope: skill.scope,
                  enabled: skill.enabled,
                  license: skill.license,
                  compatibility: skill.compatibility,
                  metadata: skill.metadata,
                  allowedTools: skill.allowedTools,
                  tags: skill.tags,
                }}
                onSave={handleUpdate}
                onCancel={() => {
                  setIsEditing(false);
                  setUpdateError(null);
                }}
                isSubmitting={updateSkill.isPending}
                isEdit={true}
                error={updateError}
                title={t('editing', { displayName: skill.displayName })}
                subtitle={t('sharpenTheDescriptionToImproveWhenPleri')}
                tagNames={tagNames}
              />
            </div>
          ) : isCopyingToTenant ? (
            <div className="p-6">
              <SkillForm
                initial={{
                  name: skill.name,
                  displayName: skill.displayName,
                  description: skill.description,
                  workspace: skill.workspace ?? [],
                  instructions: skill.instructions,
                  scope: 'tenant',
                  enabled: skill.enabled,
                  license: skill.license,
                  compatibility: skill.compatibility,
                  metadata: skill.metadata
                    ? Object.fromEntries(Object.entries(skill.metadata).filter(([k]) => !k.startsWith('_')))
                    : undefined,
                  allowedTools: skill.allowedTools,
                  tags: skill.tags,
                }}
                onSave={handleCopyToTenant}
                onCancel={() => {
                  setIsCopyingToTenant(false);
                  setCopyToTenantError(null);
                }}
                isSubmitting={createSkill.isPending}
                isEdit={false}
                isAdmin={isAdmin}
                saveLabel={t('copyToTenant')}
                error={copyToTenantError}
                title={t('copyToTenantNamed', {
                  displayName: skill.displayName,
                })}
                subtitle={t('reviewAndAdjustBeforePublishingToYour')}
                tagNames={tagNames}
              />
            </div>
          ) : isCopyingToMySkills ? (
            <div className="p-6">
              <SkillForm
                initial={{
                  name: skill.name,
                  displayName: skill.displayName,
                  description: skill.description,
                  workspace: skill.workspace ?? [],
                  instructions: skill.instructions,
                  scope: 'user',
                  enabled: skill.enabled,
                  license: skill.license,
                  compatibility: skill.compatibility,
                  metadata: skill.metadata
                    ? Object.fromEntries(Object.entries(skill.metadata).filter(([k]) => !k.startsWith('_')))
                    : undefined,
                  allowedTools: skill.allowedTools,
                  tags: skill.tags,
                }}
                onSave={handleCopyToMySkills}
                onCancel={() => {
                  setIsCopyingToMySkills(false);
                  setCopyToMySkillsError(null);
                }}
                isSubmitting={createSkill.isPending}
                isEdit={false}
                isAdmin={isAdmin}
                saveLabel={t('copyToMySkills')}
                error={copyToMySkillsError}
                title={t('copyToMySkillsNamed', {
                  displayName: skill.displayName,
                })}
                subtitle={t('customizeThisSkillForYourOwnUse')}
                tagNames={tagNames}
              />
            </div>
          ) : (
            <SkillDetail
              skill={skill}
              onEdit={() => setIsEditing(true)}
              onDelete={() => setConfirmDelete(true)}
              onExport={handleExport}
              onToggleEnabled={handleToggleEnabled}
              isDisabledByUser={isDisabledByUser}
              onToggleUserDisabled={handleToggleUserDisabled}
              onForkTemplate={
                skill.scope === 'template' ? handleFork : undefined
              }
              onCopyToTenant={
                skill.scope === 'user' && isAdmin && currentUserId != null && skill.createdBy === currentUserId
                  ? () => setIsCopyingToTenant(true)
                  : undefined
              }
              onCopyToMySkills={
                skill.scope === 'tenant' && !skill.isBuiltIn
                  ? () => setIsCopyingToMySkills(true)
                  : undefined
              }
              isForkLoading={forkTemplate.isPending}
              isCopyToTenantLoading={createSkill.isPending}
              isCopyToMySkillsLoading={createSkill.isPending}
              actionError={actionError}
              onDismissActionError={() => setActionError(null)}
              isReadOnly={isReadOnly}
              isAdmin={isAdmin}
              currentUserId={currentUserId ?? undefined}
              onTryInChat={handleTryInChat}
              onEditWithChat={!isReadOnly ? handleEditWithChat : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}
